/**
 * The cast panel (docs/SPEC.md §6.3, §6.4; P28-T14): every object the film
 * needs, one row each, beside the conversation. A row has its picture (the
 * Object's own, else the stand-in itself: its first scene drawn in the
 * browser with the view turned onto it alone, P28-T16 — never the whole
 * stage, whose product would stand for it; a neutral shape until it is drawn
 * or when it cannot be), its name, where it comes from in words ("made for {ad}" where it
 * was), the scenes it is in, and **Replace** — a picker with the tabs
 * **Objects**, **Design studio** and **Photo**. A stand-in Objects has no
 * match for is flagged and offers **Attach a photo**, **Make it in the design
 * studio** and **Use the stand-in** directly; one being made in the studio
 * says so; one whose Object arrived offers the redo with **Yes** and **Not
 * now**. Every action is a change of the one change pipeline (`api/cast.ts`):
 * the film changes through the change events, the rows through `cast`.
 *
 * People (P29): a person is a row with its figure's picture and **Replace**
 * offers figures only (any figure fits: one skeleton); a crowd is one row
 * ("A crowd of 12") whose **Replace** sets the figures it is drawn from —
 * one, several or every figure. Neither has a studio or a photo tab: a
 * figure's face is drawn.
 */
import { type ReactNode, useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { AdDetail } from "../api/ads";
import type { AssetFilters } from "../api/assetlib";
import {
  type CastRow,
  useCast,
  useFound,
  useKeep,
  alreadyThat,
  useReplace,
} from "../api/cast";
import { priceAsk, shortfallOf } from "../api/client";
import { useMe } from "../api/me";
import { useCarryThread, useModels } from "../api/models";
import type { AssetItem } from "../api/types";
import { AssetGrid } from "../assets/AssetGrid";
import {
  CAST,
  foundLine,
  originLine,
  sayCast,
  scenesLine,
  heldByLine,
  standinsLine,
} from "../copy/cast";
import { sentenceFor } from "../copy/errors";
import { sentenceOf } from "../copy/changes";
import { isPlain } from "../copy/plain";
import { frameSize } from "../film/frames";
import { useFrame } from "../film/useFrame";
import { UploadButton } from "../library/UploadButton";
import { Button, Card, Dialog, Input, Skeleton, Tabs } from "../ui";
import { PriceConfirm, TopUp } from "../ui/Credits";
import { useDebouncedValue } from "../ui/useDebouncedValue";
import { CastObjectDialog } from "./CastObjectDialog";

import { CastFilmContext, objectOf } from "./castContext";
export { CastFilmContext, objectOf } from "./castContext";

/** The short side a stand-in's picture is drawn at (the row shows 56 px). */
const THUMB_SHORT = 112;

/** The rows drawn as stand-ins and not kept: the heading counts them. */
const FLAGGED = new Set(["missing", "found", "making"]);

/** The design studio's New design page carrying the row's thread (§6.4). */
export function studioLink(adId: string, row: CastRow): string {
  const query = new URLSearchParams({
    describe: row.thing ?? row.name,
    ad: adId,
    item: row.key,
  });
  return `/assets/studio/new?${query.toString()}`;
}

/** What a row looked like when an action on it was sent: its notice shows
 *  until the row changes. */
const lookOf = (row: CastRow) => `${row.origin}|${row.state}|${row.found}`;

/** A scene's number in the film ("2"), by its id. */
function useSceneNumbers(ad: AdDetail): (ids: string[]) => string[] {
  const order = useMemo(
    () => (ad.current_version?.scenes ?? []).map((s) => s.id),
    [ad.current_version],
  );
  return (ids) =>
    ids.map((id) => {
      const k = order.indexOf(id);
      return k >= 0 ? String(k + 1) : id;
    });
}

function Thumb({ row }: { row: CastRow }) {
  const manifest = useContext(CastFilmContext);
  const aspect = manifest?.aspect ?? "9:16";
  const size = useMemo(() => frameSize(aspect, THUMB_SHORT), [aspect]);
  // no picture of its own: the object itself, in its first scene. A crowd's
  // row carries its first walker's figure (2026-10-23 fix(people) scenes);
  // without one, its scene as the film shows it (it is many people)
  const drawn = !row.thumb_url && row.scenes.length > 0;
  const framed = useFrame(
    manifest,
    row.scenes[0] ?? null,
    size,
    drawn && manifest !== null,
    drawn && row.origin !== "crowd" ? objectOf(row.key) : null,
  );
  const src = row.thumb_url ?? framed;
  return (
    <span
      className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100"
      data-testid="cast-thumb"
    >
      {src ? (
        <img
          src={src}
          alt={row.name}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      ) : (
        <svg
          viewBox="0 0 24 24"
          className="h-7 w-7 text-slate-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          role="img"
          aria-label={row.name}
          data-testid="cast-thumb-shape"
        >
          <path d="M12 3 20 7.5v9L12 21l-8-4.5v-9L12 3Z" />
          <path d="M4 7.5 12 12l8-4.5M12 12v9" />
        </svg>
      )}
    </span>
  );
}

/** A photo for the row: uploaded, then sent as its replacement. The author
 *  writes the scenes that show it (a model's work): at or above the
 *  threshold the route asks first, and below the estimate it is the block
 *  with **Top up** (§15.1). `button` goes with the row's actions, `below`
 *  (the question, the block, a refusal) under them. */
function usePhotoAction({
  adId,
  row,
  label,
  variant = "ghost",
  onSent,
}: {
  adId: string;
  row: CastRow;
  label: string;
  variant?: "primary" | "ghost";
  onSent(): void;
}): { button: ReactNode; below: ReactNode } {
  const replace = useReplace(adId);
  const [asked, setAsked] = useState<{ upload: string; credits: number }>();
  const send = (upload: string, confirm = false) => {
    setAsked(undefined);
    replace.mutate(
      { key: row.key, upload_id: upload, ...(confirm ? { confirm } : {}) },
      {
        onSuccess: (answer) => {
          const ask = priceAsk(answer);
          if (ask) setAsked({ upload, credits: ask.estimate });
          else onSent();
        },
      },
    );
  };
  const short = shortfallOf(replace.error);
  const button = (
    <UploadButton
      label={replace.isPending ? CAST.photoSending : label}
      size="sm"
      variant={variant}
      testId={`photo-input-${row.key}`}
      disabled={replace.isPending}
      onUploaded={(id) => send(id)}
    />
  );
  const below = asked ? (
    <PriceConfirm
      what="photo"
      credits={asked.credits}
      busy={replace.isPending}
      onYes={() => send(asked.upload, true)}
      onNo={() => setAsked(undefined)}
    />
  ) : short ? (
    <TopUp shortfall={short} />
  ) : replace.error ? (
    <p role="alert" className="m-0 text-sm text-slate-700">
      {sentenceFor(replace.error)}
    </p>
  ) : null;
  return { button, below };
}

/** The picker's **Photo** tab. */
function PhotoTab({
  adId,
  row,
  onSent,
}: {
  adId: string;
  row: CastRow;
  onSent(): void;
}) {
  const photo = usePhotoAction({
    adId,
    row,
    label: CAST.photoUpload,
    variant: "primary",
    onSent,
  });
  return (
    <>
      <p className="m-0 text-sm text-slate-700">{CAST.photoHelp}</p>
      <div className="flex flex-wrap items-center gap-2">{photo.button}</div>
      {photo.below}
    </>
  );
}

function TagFilters({
  initial,
  selected,
  onChange,
}: {
  initial: string[];
  selected: string[];
  onChange(tags: string[]): void;
}) {
  if (!initial.length) return null;
  return (
    <div
      role="group"
      aria-label={CAST.similarTags}
      className="flex flex-wrap items-center gap-2"
    >
      {initial.map((tag) => (
        <Button
          key={tag}
          type="button"
          size="sm"
          variant={selected.includes(tag) ? "primary" : "secondary"}
          aria-pressed={selected.includes(tag)}
          onClick={() =>
            onChange(
              selected.includes(tag)
                ? selected.filter((t) => t !== tag)
                : [...selected, tag],
            )
          }
        >
          {tag}
        </Button>
      ))}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={() => onChange([])}
      >
        {CAST.allObjects}
      </Button>
    </div>
  );
}

/** **Replace**: the picker's three tabs. */
function ReplaceDialog({
  ad,
  row,
  open,
  onClose,
  onSent,
}: {
  ad: AdDetail;
  row: CastRow;
  open: boolean;
  onClose(): void;
  onSent(): void;
}) {
  const me = useMe();
  const features = me.data?.features;
  const [tab, setTab] = useState("objects");
  const [typed, setTyped] = useState(row.thing ?? "");
  const [tags, setTags] = useState(row.tags ?? []);
  const q = useDebouncedValue(typed, 300).trim();
  const [chosen, setChosen] = useState<AssetItem | null>(null);
  const [already, setAlready] = useState<string | null>(null);
  const replace = useReplace(ad.id);
  const filters = useMemo<AssetFilters>(
    () => ({
      kind: "object",
      ...(q ? { q } : {}),
      ...(tags.length ? { tags } : {}),
    }),
    [q, tags],
  );
  const use = () => {
    if (!chosen) return;
    setAlready(null);
    replace.mutate(
      { key: row.key, ref: `objects:${chosen.id}@${chosen.version}` },
      {
        onSuccess: (answer) => {
          const same = alreadyThat(answer);
          if (same) {
            setAlready(same);
            return;
          }
          onSent();
          onClose();
        },
      },
    );
  };
  const tabs = [
    { id: "objects", label: CAST.tabObjects },
    { id: "studio", label: CAST.tabStudio },
    { id: "photo", label: CAST.tabPhoto },
  ];
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={sayCast(CAST.pickerTitle, { name: row.name })}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            {CAST.cancel}
          </Button>
          {tab === "objects" ? (
            <Button
              type="button"
              variant="primary"
              disabled={!chosen || replace.isPending}
              aria-busy={replace.isPending}
              className="disabled:cursor-not-allowed disabled:opacity-50"
              onClick={use}
            >
              {CAST.useThis}
            </Button>
          ) : null}
        </>
      }
    >
      <Tabs tabs={tabs} active={tab} onChange={setTab}>
        <div className="flex flex-col gap-3 pt-4" data-testid={`tab-${tab}`}>
          {tab === "objects" ? (
            features?.assetlib === false ? (
              <p className="m-0 text-sm text-slate-700">{CAST.notAvailable}</p>
            ) : (
              <>
                <p className="m-0 text-sm text-slate-700">{CAST.objectsHelp}</p>
                <Input
                  type="search"
                  aria-label={CAST.search}
                  placeholder={CAST.search}
                  className="min-h-[44px] sm:min-h-0 sm:max-w-xs"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                />
                <TagFilters
                  initial={row.tags ?? []}
                  selected={tags}
                  onChange={setTags}
                />
                <AssetGrid
                  filters={filters}
                  select={{
                    kinds: ["shape", "variant"],
                    onSelect: (item) =>
                      setChosen((was) =>
                        was && was.kind === item.kind && was.id === item.id
                          ? null
                          : item,
                      ),
                    selected: chosen ? [`${chosen.kind}:${chosen.id}`] : [],
                    max: 1,
                  }}
                  onClearSearch={() => {
                    setTyped("");
                    setTags([]);
                  }}
                />
                {already ? (
                  <p
                    role="status"
                    className="m-0 text-sm text-slate-700"
                    data-testid="cast-already"
                  >
                    {already}
                  </p>
                ) : null}
                {replace.error ? (
                  <p role="alert" className="m-0 text-sm text-slate-700">
                    {sentenceFor(replace.error)}
                  </p>
                ) : null}
              </>
            )
          ) : tab === "studio" ? (
            <StudioTab ad={ad} row={row} />
          ) : (
            <PhotoTab
              adId={ad.id}
              row={row}
              onSent={() => {
                onSent();
                onClose();
              }}
            />
          )}
        </div>
      </Tabs>
    </Dialog>
  );
}

/** Whether a row is a person or a crowd: its picker offers figures only. */
const isPeople = (row: CastRow) =>
  row.origin === "figure" || row.origin === "crowd";

/** **Replace** on a person or a crowd (P29, §P29.4 (5)): Objects' People
 *  as a picker, with **Use this one**. A person takes one figure; a crowd
 *  is drawn from one, several, or **Every figure**. No design studio and no
 *  photo: a figure's face is drawn. */
function FigureDialog({
  ad,
  row,
  open,
  onClose,
  onSent,
}: {
  ad: AdDetail;
  row: CastRow;
  open: boolean;
  onClose(): void;
  onSent(): void;
}) {
  const features = useMe().data?.features;
  const crowd = row.origin === "crowd";
  const [typed, setTyped] = useState("");
  const [tags, setTags] = useState(row.tags ?? []);
  const q = useDebouncedValue(typed, 300).trim();
  const [chosen, setChosen] = useState<AssetItem[]>([]);
  const [every, setEvery] = useState(false);
  const [already, setAlready] = useState<string | null>(null);
  const replace = useReplace(ad.id);
  const filters = useMemo<AssetFilters>(
    () => ({
      kind: "figure",
      ...(q ? { q } : {}),
      ...(tags.length ? { tags } : {}),
    }),
    [q, tags],
  );
  const refOf = (item: AssetItem) => `figure:${item.id}@${item.version}`;
  const toggle = (item: AssetItem) => {
    setEvery(false);
    setChosen((was) =>
      was.some((c) => c.id === item.id)
        ? was.filter((c) => c.id !== item.id)
        : crowd
          ? [...was, item]
          : [item],
    );
  };
  const ready = every || chosen.length > 0;
  const several = crowd && (every || chosen.length > 1);
  const use = () => {
    if (!ready) return;
    const body = every
      ? { figures: [] }
      : several
        ? { figures: chosen.map(refOf) }
        : { ref: refOf(chosen[0]) };
    setAlready(null);
    replace.mutate(
      { key: row.key, ...body },
      {
        onSuccess: (answer) => {
          const same = alreadyThat(answer);
          if (same) {
            setAlready(same);
            return;
          }
          onSent();
          onClose();
        },
      },
    );
  };
  const status = every
    ? CAST.crowdEveryHelp
    : chosen.length === 1
      ? CAST.crowdChosenOne
      : sayCast(CAST.crowdChosenMany, { count: chosen.length });
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={sayCast(CAST.pickerTitle, { name: row.name })}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            {CAST.cancel}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={!ready || replace.isPending}
            aria-busy={replace.isPending}
            className="disabled:cursor-not-allowed disabled:opacity-50"
            onClick={use}
          >
            {several ? CAST.useThese : CAST.useThis}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="tab-people">
        {features?.assetlib === false ? (
          <p className="m-0 text-sm text-slate-700">{CAST.notAvailable}</p>
        ) : (
          <>
            <p className="m-0 text-sm text-slate-700">
              {crowd ? CAST.crowdHelp : CAST.peopleHelp}
            </p>
            <p className="m-0 text-xs text-slate-500">{CAST.drawnNotPhoto}</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                type="search"
                aria-label={CAST.searchPeople}
                placeholder={CAST.searchPeople}
                className="min-h-[44px] sm:min-h-0 sm:max-w-xs"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
              />
              {crowd ? (
                <Button
                  type="button"
                  size="sm"
                  variant={every ? "primary" : "secondary"}
                  aria-pressed={every}
                  className="min-h-[44px] sm:min-h-0"
                  onClick={() => {
                    setEvery((was) => !was);
                    setChosen([]);
                  }}
                >
                  {CAST.crowdEvery}
                </Button>
              ) : null}
            </div>
            {crowd && ready ? (
              <p
                role="status"
                className="m-0 text-sm font-semibold text-slate-800"
              >
                {status}
              </p>
            ) : null}
            <TagFilters
              initial={row.tags ?? []}
              selected={tags}
              onChange={setTags}
            />
            <AssetGrid
              filters={filters}
              select={{
                kinds: ["figure"],
                onSelect: toggle,
                selected: chosen.map((c) => `figure:${c.id}`),
                max: crowd ? 24 : 1,
              }}
              onClearSearch={() => {
                setTyped("");
                setTags([]);
              }}
            />
            {already ? (
              <p
                role="status"
                className="m-0 text-sm text-slate-700"
                data-testid="cast-already"
              >
                {already}
              </p>
            ) : null}
            {replace.error ? (
              <p role="alert" className="m-0 text-sm text-slate-700">
                {sentenceFor(replace.error)}
              </p>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

/** The picker's **Design studio** tab: a new design for the row, or one of
 *  the person's designs opened for it (`PATCH /v1/models/{id} {for_ad}`). */
function StudioTab({ ad, row }: { ad: AdDetail; row: CastRow }) {
  const me = useMe();
  const navigate = useNavigate();
  const models = useModels();
  const carry = useCarryThread();
  if (me.data?.features?.studio === false) {
    return <p className="m-0 text-sm text-slate-700">{CAST.notAvailable}</p>;
  }
  // a scene's own object (`<scene>.<ref>`) has no cast line to carry
  if (row.key.includes(".")) {
    return <p className="m-0 text-sm text-slate-700">{CAST.studioOwnObject}</p>;
  }
  const designs = models.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <>
      <p className="m-0 text-sm text-slate-700">
        {sayCast(CAST.studioHelp, { ad: ad.title })}
      </p>
      <div>
        <Button as={Link} to={studioLink(ad.id, row)} variant="primary">
          {CAST.studioNew}
        </Button>
      </div>
      <h3 className="m-0 text-sm font-semibold text-slate-900">
        {CAST.studioMine}
      </h3>
      {models.isPending ? (
        <Skeleton height={44} />
      ) : designs.length === 0 ? (
        <p className="m-0 text-sm text-slate-600">{CAST.studioNone}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {designs.map((design) => (
            <li
              key={design.id}
              className="flex items-center gap-3 rounded-lg border border-slate-200 p-2"
            >
              <span className="block h-11 w-11 shrink-0 overflow-hidden rounded bg-slate-100">
                {design.thumb_url ? (
                  <img
                    src={design.thumb_url}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : null}
              </span>
              <span className="min-w-0 flex-1 break-words text-sm text-slate-800">
                {design.name}
              </span>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={carry.isPending}
                onClick={() =>
                  carry.mutate(
                    {
                      id: design.id,
                      for_ad: { ad_id: ad.id, item: row.key },
                    },
                    {
                      onSuccess: () =>
                        navigate(
                          `/assets/studio/${encodeURIComponent(design.id)}`,
                        ),
                    },
                  )
                }
              >
                {CAST.studioOpen}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {carry.error ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {sentenceFor(carry.error)}
        </p>
      ) : null}
    </>
  );
}

function RowView({
  ad,
  row,
  numbers,
  numbersOf,
  notice,
  onSent,
}: {
  ad: AdDetail;
  row: CastRow;
  numbers: string[];
  numbersOf(ids: string[]): string[];
  notice: boolean;
  onSent(): void;
}) {
  const me = useMe();
  const studio = me.data?.features?.studio !== false;
  const keep = useKeep(ad.id);
  const found = useFound(ad.id);
  const [replacing, setReplacing] = useState(false);
  const [viewing, setViewing] = useState(false);
  const thing = row.thing ?? row.name;
  const flagged = row.state === "missing";
  const held = heldByLine(
    (row.held_by ?? []).map((h) => ({ ...h, scenes: numbersOf(h.scenes) })),
  );
  const error = keep.error ?? found.error;
  const [acceptedFor, setAcceptedFor] = useState<string | null>(null);
  const acceptFound = () => {
    setAcceptedFor(row.found);
    found.mutate({ key: row.key, use: true });
  };
  const accepted =
    found.variables?.use && acceptedFor === row.found
      ? found.data?.change
      : null;
  const replacement =
    row.state === "found"
      ? accepted && (!row.replacement || accepted.n > row.replacement.n)
        ? accepted
        : row.replacement
      : null;
  const replacementFailed =
    replacement &&
    ["cannot", "failed", "cancelled", "declined"].includes(replacement.state);
  const failureDetail = replacementFailed
    ? sentenceOf({
        code: replacement.error_code ?? "change_failed",
        params: replacement.error_params ?? {},
      })
    : null;
  const photo = usePhotoAction({
    adId: ad.id,
    row,
    label: CAST.attachPhoto,
    onSent,
  });
  return (
    <li
      className="flex gap-3 border-t border-slate-100 px-5 py-3 first:border-t-0"
      data-testid={`cast-row-${row.key}`}
      data-state={row.state}
    >
      <Thumb row={row} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="m-0 break-words text-sm font-semibold text-slate-900">
          {row.name}
        </p>
        <p className="m-0 text-xs text-slate-600" data-testid="cast-origin">
          {originLine(row.origin, row.made_for, ad.id)}
        </p>
        {held ? (
          <p className="m-0 text-xs text-slate-600" data-testid="cast-held">
            {held}
          </p>
        ) : null}
        <p className="m-0 text-xs text-slate-500" data-testid="cast-scenes">
          {scenesLine(numbers)}
        </p>

        {flagged ? (
          <div
            className="mt-1 flex flex-col gap-1 rounded-lg border border-amber-200 bg-amber-50 p-2"
            data-testid="cast-flag"
          >
            <p className="m-0 text-sm font-semibold text-slate-900">
              {sayCast(CAST.missing, { thing })}
            </p>
            <p className="m-0 text-xs text-slate-700">{CAST.missingHelp}</p>
          </div>
        ) : null}
        {row.state === "kept" ? (
          <p className="m-0 text-xs text-slate-600">{CAST.kept}</p>
        ) : null}
        {row.state === "making" ? (
          <div
            className="mt-1 flex flex-col items-start gap-1 rounded-lg border border-sky-200 bg-sky-50 p-2"
            data-testid="cast-making"
          >
            <p className="m-0 text-sm font-semibold text-slate-900">
              {CAST.making}
            </p>
            <p className="m-0 text-xs text-slate-700">
              {sayCast(CAST.makingHelp, { ad: ad.title })}
            </p>
            <Link
              to={
                row.design
                  ? `/assets/studio/${encodeURIComponent(row.design)}`
                  : "/assets/studio"
              }
              className="inline-flex min-h-[44px] items-center text-sm font-semibold underline md:min-h-0"
            >
              {CAST.openInStudio}
            </Link>
          </div>
        ) : null}
        {replacement ? (
          <div
            className="mt-1 flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2"
            data-testid="cast-replacement"
          >
            <p
              className="m-0 text-sm"
              role={replacementFailed ? "alert" : "status"}
            >
              {replacementFailed
                ? CAST.replacementFailed
                : replacement.state === "applied"
                  ? CAST.replacementApplied
                  : replacement.state === "building"
                    ? CAST.replacementBuilding
                    : ["waiting_confirm", "answered"].includes(
                          replacement.state,
                        )
                      ? CAST.replacementApproval
                      : replacement.state === "queued"
                        ? CAST.replacementQueued
                        : CAST.replacementWorking}
            </p>
            {replacementFailed && failureDetail && isPlain(failureDetail) ? (
              <p className="m-0 text-sm text-slate-600">{failureDetail}</p>
            ) : null}
            {replacementFailed ? (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  disabled={found.isPending}
                  onClick={acceptFound}
                >
                  {CAST.retryReplacement}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={found.isPending}
                  onClick={() => found.mutate({ key: row.key, use: false })}
                >
                  {CAST.notNow}
                </Button>
              </div>
            ) : null}
          </div>
        ) : row.state === "found" ? (
          <div
            className="mt-1 flex flex-col gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-2"
            data-testid="cast-found"
            role="group"
            aria-label={CAST.foundLabel}
          >
            <p className="m-0 text-sm font-semibold text-slate-900">
              {foundLine(thing, numbers)}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="primary"
                disabled={found.isPending}
                onClick={acceptFound}
              >
                {CAST.yes}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={found.isPending}
                onClick={() => found.mutate({ key: row.key, use: false })}
              >
                {CAST.notNow}
              </Button>
            </div>
          </div>
        ) : null}

        {notice && !replacement ? (
          <p role="status" className="m-0 text-xs text-slate-700">
            {CAST.sent}
          </p>
        ) : null}

        <div className="mt-1 flex flex-wrap items-center gap-2 max-sm:[&_.tb-btn]:min-h-[44px]">
          {flagged ? (
            <>
              {photo.button}
              {studio ? (
                <Button
                  as={Link}
                  to={studioLink(ad.id, row)}
                  size="sm"
                  variant="ghost"
                >
                  {CAST.makeInStudio}
                </Button>
              ) : null}
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={keep.isPending}
                onClick={() => keep.mutate(row.key)}
              >
                {CAST.useStandin}
              </Button>
            </>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={sayCast(CAST.viewObject, { name: row.name })}
            onClick={() => setViewing(true)}
          >
            {CAST.view}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setReplacing(true)}
          >
            {CAST.replace}
          </Button>
        </div>
        {flagged ? photo.below : null}
        {error ? (
          <p role="alert" className="m-0 text-sm text-slate-700">
            {sentenceFor(error)}
          </p>
        ) : null}
      </div>
      {viewing ? (
        <CastObjectDialog row={row} onClose={() => setViewing(false)} />
      ) : null}
      {replacing && isPeople(row) ? (
        <FigureDialog
          ad={ad}
          row={row}
          open
          onClose={() => setReplacing(false)}
          onSent={onSent}
        />
      ) : replacing ? (
        <ReplaceDialog
          ad={ad}
          row={row}
          open
          onClose={() => setReplacing(false)}
          onSent={onSent}
        />
      ) : null}
    </li>
  );
}

export function CastPanel({ ad }: { ad: AdDetail }) {
  const cast = useCast(ad.id);
  const numbersOf = useSceneNumbers(ad);
  /** The rows an action was sent for, as they looked then. */
  const [sent, setSent] = useState<Record<string, string>>({});
  const rows = cast.data?.rows ?? [];
  const flagged = rows.filter((r) => FLAGGED.has(r.state)).length;
  return (
    <Card className="flex flex-col p-0" data-testid="cast-panel">
      <div className="flex flex-col gap-1 px-5 pb-2 pt-4">
        <h2 className="m-0 flex flex-wrap items-center gap-2 text-base font-semibold text-slate-900">
          <span>{CAST.heading}</span>
          {flagged > 0 ? (
            <span
              className="text-sm font-normal text-amber-700"
              data-testid="cast-count"
            >
              {standinsLine(flagged)}
            </span>
          ) : null}
        </h2>
        <p className="m-0 text-xs text-slate-500">{CAST.intro}</p>
      </div>
      {cast.isPending ? (
        <div className="flex flex-col gap-2 px-5 pb-4" aria-busy="true">
          <span className="sr-only">{CAST.loading}</span>
          <Skeleton height={56} />
          <Skeleton height={56} />
        </div>
      ) : cast.isError ? (
        <p role="alert" className="m-0 px-5 pb-4 text-sm text-slate-700">
          {sentenceFor(cast.error)}
        </p>
      ) : rows.length === 0 ? (
        <p className="m-0 px-5 pb-4 text-sm text-slate-600">{CAST.none}</p>
      ) : (
        <ul className="m-0 list-none p-0 pb-1">
          {rows.map((row) => (
            <RowView
              key={row.key}
              ad={ad}
              row={row}
              numbers={numbersOf(row.scenes)}
              numbersOf={numbersOf}
              notice={sent[row.key] === lookOf(row)}
              onSent={() => setSent((s) => ({ ...s, [row.key]: lookOf(row) }))}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}
